/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import { CollectionsModule, MODULE_LAYOUTS } from '@coursera-web/cds-labs';

import { isAuthenticatedUser } from 'js/lib/user';

import { Button } from '@coursera/cds-core';
import type { ButtonName } from '@coursera/event-pulse-types';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import type { CarouselCollection } from 'bundles/collections-carousel/types/CarouselCollection';
import PrePurchaseUpsellCourseraPlusModal from 'bundles/coursera-plus/components/prePurchaseUpsell/PrePurchaseUpsellCourseraPlusModal';
import { useCourseraPlusSubscriptionFreeTrials } from 'bundles/coursera-plus/hooks/useCourseraPlusSubscriptionFreeTrials';
import { getLandingPageLink } from 'bundles/coursera-plus/utils/courseraPlusUtils';
import { useSpecializationPageData } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffSpecializationContext';
import { useXdpRecommendations } from 'bundles/unified-description-page-common/providers/specialization/RecommendationsDataProvider';

import CourseraPlusLogo from 'images/coursera-plus/coursera-plus-blue.png';

// eslint-disable-next-line @coursera-web/custom-rules/i18n-import-rule
import _t from 'i18n!nls/xdp';

const CourseraPlusRecommendationsModule = () => {
  const xdpRecommendationsData = useXdpRecommendations();
  const track = useTracker();
  const visibilityTrackingRef = useVisibilityTracker('view_page_section', {
    pageSection: { sectionName: 'upsell' },
  });
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const specializationPageData = useSpecializationPageData();
  const { userHasCourseraPlusFreeTrial: userIsEligibleForFreeTrial } = useCourseraPlusSubscriptionFreeTrials();

  if (!xdpRecommendationsData?.recommendationCollectionEntities?.length) return null;

  const { recommendationCollectionEntities, collectionTrackingId, eventingRecommender, loading } =
    xdpRecommendationsData;
  const title = _t('Get access to all these courses and more with a subscription');

  const recommendationEntitiesToUse = recommendationCollectionEntities.filter((entity) => {
    if (specializationPageData && entity.slug === specializationPageData.slug) {
      // don't show the same specialization as the one being viewed
      return false;
    }
    return true;
  });

  const recommendationEntitiesFilterOutBadges = recommendationEntitiesToUse.map((entity) => {
    return {
      ...entity,
      badges: [],
    };
  });

  const carouselCollection: CarouselCollection = {
    title,
    collectionId: collectionTrackingId,
    entities: recommendationEntitiesFilterOutBadges,
    collectionTrackingData: {
      carouselRecommender: eventingRecommender,
    },
    renderCustomPrimaryCta: ({ size, fullWidth, variantOverride }) => {
      const isAuthenticated = isAuthenticatedUser();
      let buttonText = '';
      let buttonName: ButtonName;

      if (isAuthenticated) {
        if (userIsEligibleForFreeTrial) {
          buttonText = _t('Start free trial');
          buttonName = 'start_free_trial';
        } else {
          buttonText = _t('Renew subscription');
          buttonName = 'enroll';
        }
      } else {
        buttonText = _t('Learn more');
        buttonName = 'c_plus_learn_more';
      }

      const onClick = () => {
        track('click_button', {
          button: {
            name: buttonName,
          },
        });
        if (isAuthenticated) {
          setIsModalOpen(true);
        }
      };

      if (!isAuthenticated) {
        return (
          <Button
            component="a"
            size={size}
            fullWidth={fullWidth}
            variant={variantOverride ?? 'primary'}
            onClick={onClick}
            href={getLandingPageLink()}
          >
            {buttonText}
          </Button>
        );
      }

      return (
        <Button size={size} fullWidth={fullWidth} variant={variantOverride ?? 'primary'} onClick={onClick}>
          {buttonText}
        </Button>
      );
    },
  };

  return (
    <React.Fragment>
      <PrePurchaseUpsellCourseraPlusModal
        handleClose={() => setIsModalOpen(false)}
        isOpen={isModalOpen}
        hasCourseraPlusFreeTrial={userIsEligibleForFreeTrial}
      />
      <div ref={visibilityTrackingRef as React.MutableRefObject<HTMLDivElement>}>
        <CollectionsModule
          headline={title}
          isCollectionsLoading={loading}
          collections={[carouselCollection]}
          layout={MODULE_LAYOUTS.textTopImageBottom}
          eyebrow={{
            image: {
              url: CourseraPlusLogo,
              height: 17.5,
              description: _t('Coursera Plus'),
            },
          }}
        />
      </div>
    </React.Fragment>
  );
};

export default CourseraPlusRecommendationsModule;
